import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPosSale, fetchPosCatalog, fetchPosSales, PAYMENT_METHODS, voidPosSale } from '../../api/pos';
import { fetchBranches } from '../../api/salon';
import { useToast } from '../../context/ToastContext';

const formatLkr = (n) => `LKR ${Number(n || 0).toLocaleString()}`;
const uid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const staffExtraForService = (staff, serviceId) => {
  const row = (staff.serviceSurcharges || []).find((s) => s.serviceId === serviceId);
  return Number(row?.extraAmount) || 0;
};

const SalonPosPage = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [branchId, setBranchId] = useState('');
  const [tab, setTab] = useState('all');
  const [cartOpen, setCartOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [cart, setCart] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState('CASH');
  const [walkInName, setWalkInName] = useState('');
  const [walkInPhone, setWalkInPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [discount, setDiscount] = useState('');
  const [staffPick, setStaffPick] = useState(null);
  const [lastSale, setLastSale] = useState(null);
  const [voidTarget, setVoidTarget] = useState(null);
  const [voidReason, setVoidReason] = useState('');

  const { data: branchData } = useQuery({
    queryKey: ['salon-branches'],
    queryFn: fetchBranches,
  });
  const branches = useMemo(() => branchData?.branches || [], [branchData]);

  useEffect(() => {
    if (branchId || !branches.length) return;
    const main = branches.find((b) => b.isMain) || branches[0];
    setBranchId(main._id);
  }, [branches, branchId]);

  const { data: catalog, isLoading: catalogLoading } = useQuery({
    queryKey: ['pos-catalog', branchId],
    queryFn: () => fetchPosCatalog(branchId),
    enabled: !!branchId,
  });

  const { data: salesData, refetch: refetchSales } = useQuery({
    queryKey: ['pos-sales', branchId],
    queryFn: () => fetchPosSales({ branchId, limit: 20 }),
    enabled: !!branchId,
  });

  const products = catalog?.products || [];
  const services = catalog?.services || [];
  const staffList = catalog?.staff || [];
  const recentSales = salesData?.sales || [];

  const needle = search.trim().toLowerCase();
  const filteredProducts = products.filter((p) => !needle || p.name.toLowerCase().includes(needle));
  const filteredServices = services.filter((s) => !needle || s.name.toLowerCase().includes(needle));

  const cartSubtotal = useMemo(
    () => cart.reduce((sum, line) => sum + line.lineTotal, 0),
    [cart]
  );
  const cartCounts = useMemo(
    () => ({
      services: cart.filter((line) => line.lineType === 'SERVICE').length,
      products: cart.filter((line) => line.lineType === 'PRODUCT').length,
    }),
    [cart]
  );
  const discountAmount = Math.max(0, Number(discount) || 0);
  const cartTotal = Math.max(0, cartSubtotal - discountAmount);

  const openCartOnDesktop = () => {
    if (window.matchMedia('(min-width: 1024px)').matches) setCartOpen(true);
  };

  const renderProductGrid = (items) => (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4">
      {items.map((p) => (
        <button
          key={p._id}
          type="button"
          disabled={p.stock <= 0}
          onClick={() => addProduct(p)}
          className="rounded-xl border border-zinc-200 bg-white p-3 text-left transition hover:border-glow-500 disabled:opacity-40 dark:border-zinc-800 dark:bg-zinc-900"
        >
          <p className="font-medium line-clamp-2">{p.name}</p>
          <p className="mt-1 text-sm text-glow-400">{formatLkr(p.price)}</p>
          <p className="mt-1 text-xs text-zinc-500">{t('pos.stock', { count: p.stock })}</p>
        </button>
      ))}
    </div>
  );

  const renderServiceGrid = (items) => (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4">
      {items.map((s) => (
        <button
          key={s._id}
          type="button"
          onClick={() => openServiceStaffPick(s)}
          className="rounded-xl border border-zinc-200 bg-white p-3 text-left transition hover:border-glow-500 dark:border-zinc-800 dark:bg-zinc-900"
        >
          <p className="font-medium line-clamp-2">{s.name}</p>
          <p className="mt-1 text-sm text-glow-400">{formatLkr(s.price)}</p>
          <p className="mt-1 text-xs text-zinc-500">{t('common.duration', { minutes: s.duration })}</p>
        </button>
      ))}
    </div>
  );

  const addProduct = (product) => {
    openCartOnDesktop();
    const unit = Number(product.price || 0);
    setCart((prev) => {
      const existing = prev.find((l) => l.lineType === 'PRODUCT' && l.productId === product._id);
      if (existing) {
        return prev.map((l) =>
          l.key === existing.key
            ? {
                ...l,
                quantity: l.quantity + 1,
                lineTotal: (l.quantity + 1) * l.unitTotal,
              }
            : l
        );
      }
      return [
        ...prev,
        {
          key: uid(),
          lineType: 'PRODUCT',
          productId: product._id,
          name: product.name,
          quantity: 1,
          baseUnitPrice: unit,
          staffSurchargeAmount: 0,
          staffId: null,
          staffName: '',
          unitTotal: unit,
          lineTotal: unit,
          maxStock: product.stock,
        },
      ];
    });
  };

  const openServiceStaffPick = (service) => {
    const eligible = staffList.filter((s) => s.serviceIds.includes(service._id));
    if (!eligible.length) {
      addServiceToCart(service, null, 0, '');
      return;
    }
    setStaffPick({ service, eligible });
  };

  const addServiceToCart = (service, staffId, staffSurcharge, staffName) => {
    openCartOnDesktop();
    const base = Number(service.price || 0);
    const unitTotal = base + staffSurcharge;
    setCart((prev) => [
      ...prev,
      {
        key: uid(),
        lineType: 'SERVICE',
        serviceId: service._id,
        name: service.name,
        quantity: 1,
        baseUnitPrice: base,
        staffSurchargeAmount: staffSurcharge,
        staffId,
        staffName,
        unitTotal,
        lineTotal: unitTotal,
      },
    ]);
    setStaffPick(null);
  };

  const updateQty = (key, delta) => {
    setCart((prev) =>
      prev
        .map((line) => {
          if (line.key !== key) return line;
          const nextQty = line.quantity + delta;
          if (nextQty < 1) return null;
          if (line.maxStock != null && nextQty > line.maxStock) return line;
          return { ...line, quantity: nextQty, lineTotal: nextQty * line.unitTotal };
        })
        .filter(Boolean)
    );
  };

  const removeLine = (key) => setCart((prev) => prev.filter((l) => l.key !== key));

  const checkoutMut = useMutation({
    mutationFn: () =>
      createPosSale({
        branchId,
        paymentMethod,
        walkInName,
        walkInPhone,
        notes,
        saleAdjustment:
          discountAmount > 0
            ? {
                adjustmentType: 'DISCOUNT',
                adjustmentValue: discountAmount,
                adjustmentMode: 'FLAT',
                adjustmentReason: 'POS discount',
              }
            : undefined,
        items: cart.map((line) => ({
          lineType: line.lineType,
          productId: line.productId,
          serviceId: line.serviceId,
          quantity: line.quantity,
          staffId: line.staffId,
        })),
      }),
    onSuccess: (res) => {
      setLastSale(res.sale);
      setCart([]);
      setDiscount('');
      setNotes('');
      showToast(t('pos.saleComplete', { number: res.sale.saleNumber }), 'success');
      queryClient.invalidateQueries({ queryKey: ['pos-catalog', branchId] });
      refetchSales();
    },
    onError: (err) => showToast(err.response?.data?.message || t('pos.saleFailed'), 'error'),
  });

  const voidMut = useMutation({
    mutationFn: ({ id, reason }) => voidPosSale(id, reason),
    onSuccess: () => {
      showToast(t('pos.voided'), 'success');
      setVoidTarget(null);
      setVoidReason('');
      refetchSales();
      queryClient.invalidateQueries({ queryKey: ['pos-catalog', branchId] });
    },
    onError: (err) => showToast(err.response?.data?.message || t('pos.voidFailed'), 'error'),
  });

  const closePos = () => {
    if (window.opener) window.close();
    else window.location.href = '/salon/dashboard';
  };

  return (
    <div className="flex h-full flex-col lg:flex-row">
      <section className="flex min-h-0 min-w-0 flex-1 flex-col">
        <div className="flex flex-wrap items-center gap-3 border-b border-zinc-200 px-4 py-3 dark:border-zinc-800">
          <span className="text-sm font-semibold text-glow-400">{t('pos.title')}</span>
          <select
            value={branchId}
            onChange={(e) => {
              setBranchId(e.target.value);
              setCart([]);
            }}
            className="rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-900"
          >
            {branches.map((b) => (
              <option key={b._id} value={b._id}>{b.name}</option>
            ))}
          </select>
          <div className="flex rounded-lg border border-zinc-300 p-0.5 dark:border-zinc-700">
            <button
              type="button"
              onClick={() => setTab('all')}
              className={`rounded-md px-3 py-1 text-sm ${tab === 'all' ? 'bg-glow-600/25 text-glow-300' : ''}`}
            >
              {t('pos.allItems')}
            </button>
            <button
              type="button"
              onClick={() => setTab('services')}
              className={`rounded-md px-3 py-1 text-sm ${tab === 'services' ? 'bg-glow-600/25 text-glow-300' : ''}`}
            >
              {t('pos.services')}
            </button>
            <button
              type="button"
              onClick={() => setTab('products')}
              className={`rounded-md px-3 py-1 text-sm ${tab === 'products' ? 'bg-glow-600/25 text-glow-300' : ''}`}
            >
              {t('pos.products')}
            </button>
          </div>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('pos.search')}
            className="min-w-[140px] flex-1 rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-900"
          />
          <button
            type="button"
            onClick={() => setCartOpen((v) => !v)}
            className="rounded-lg border border-glow-600/40 bg-glow-600/10 px-3 py-2 text-sm text-glow-300"
          >
            {t('pos.cart')} ({cart.length}) · {formatLkr(cartTotal)}
          </button>
          <button
            type="button"
            onClick={closePos}
            className="rounded-lg border border-zinc-300 px-3 py-2 text-sm text-zinc-500 dark:border-zinc-700"
            title={t('pos.close')}
          >
            ✕
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          {catalogLoading && <p className="text-zinc-500">{t('common.loading')}</p>}
          {!catalogLoading && (tab === 'all' || tab === 'services') && (
            <div className={tab === 'all' ? 'mb-8' : ''}>
              {tab === 'all' && (
                <h3 className="mb-3 text-sm font-semibold text-zinc-600 dark:text-zinc-400">{t('pos.services')}</h3>
              )}
              {!filteredServices.length ? (
                <p className="text-sm text-zinc-500">{t('pos.noServices')}</p>
              ) : (
                renderServiceGrid(filteredServices)
              )}
            </div>
          )}
          {!catalogLoading && (tab === 'all' || tab === 'products') && (
            <div>
              {tab === 'all' && (
                <h3 className="mb-3 text-sm font-semibold text-zinc-600 dark:text-zinc-400">{t('pos.products')}</h3>
              )}
              {!filteredProducts.length ? (
                <p className="text-sm text-zinc-500">{t('pos.noProducts')}</p>
              ) : (
                renderProductGrid(filteredProducts)
              )}
            </div>
          )}
        </div>
      </section>

      <aside
        className={`flex w-full flex-col border-zinc-200 dark:border-zinc-800 lg:w-[380px] lg:border-l ${
          cartOpen
            ? 'fixed inset-0 z-30 bg-zinc-100 dark:bg-zinc-950 lg:static lg:z-auto lg:flex lg:bg-transparent'
            : 'hidden'
        }`}
      >
        {cartOpen && (
          <button
            type="button"
            onClick={() => setCartOpen(false)}
            className="absolute right-4 top-3 text-zinc-500 lg:hidden"
            aria-label={t('common.close')}
          >
            ✕
          </button>
        )}
        <div className="hidden border-b border-zinc-200 px-4 py-3 dark:border-zinc-800 lg:block">
          <div className="flex items-center justify-between gap-2">
            <div>
              <h2 className="font-semibold">{t('pos.cart')}</h2>
              <p className="text-xs text-zinc-500">{t('pos.cartHint')}</p>
              {cart.length > 0 && (
                <p className="mt-1 text-xs text-glow-400">
                  {t('pos.cartMix', {
                    services: cartCounts.services,
                    products: cartCounts.products,
                  })}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={() => setCartOpen(false)}
              className="rounded-lg border border-zinc-300 px-2 py-1 text-xs text-zinc-500 dark:border-zinc-700"
            >
              {t('pos.hideCart')}
            </button>
          </div>
        </div>
        <div className="border-b border-zinc-200 px-4 py-3 dark:border-zinc-800 lg:hidden">
          <h2 className="font-semibold">{t('pos.cart')}</h2>
        </div>

        <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-4">
          {!cart.length && (
            <p className="text-sm text-zinc-500">{t('pos.cartEmpty')}</p>
          )}
          {cart.map((line) => (
            <div key={line.key} className="rounded-lg border border-zinc-200 p-3 text-sm dark:border-zinc-800">
              <div className="flex justify-between gap-2">
                <div className="min-w-0">
                  <span className="mr-2 rounded bg-zinc-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-zinc-500 dark:bg-zinc-800">
                    {line.lineType === 'SERVICE' ? t('pos.lineService') : t('pos.lineProduct')}
                  </span>
                  <p className="mt-1 font-medium">{line.name}</p>
                </div>
                <button type="button" onClick={() => removeLine(line.key)} className="text-zinc-500">✕</button>
              </div>
              {line.staffSurchargeAmount > 0 && (
                <p className="text-xs text-amber-500">
                  +{formatLkr(line.staffSurchargeAmount)} ({line.staffName})
                </p>
              )}
              <div className="mt-2 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => updateQty(line.key, -1)} className="rounded border px-2">−</button>
                  <span>{line.quantity}</span>
                  <button type="button" onClick={() => updateQty(line.key, 1)} className="rounded border px-2">+</button>
                </div>
                <span className="font-medium">{formatLkr(line.lineTotal)}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="space-y-3 border-t border-zinc-200 p-4 dark:border-zinc-800">
          <input
            type="text"
            value={walkInName}
            onChange={(e) => setWalkInName(e.target.value)}
            placeholder={t('pos.walkInName')}
            className="w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-900"
          />
          <input
            type="text"
            value={walkInPhone}
            onChange={(e) => setWalkInPhone(e.target.value)}
            placeholder={t('pos.walkInPhone')}
            className="w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-900"
          />
          <select
            value={paymentMethod}
            onChange={(e) => setPaymentMethod(e.target.value)}
            className="w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-900"
          >
            {PAYMENT_METHODS.map((m) => (
              <option key={m} value={m}>{t(`pos.payment.${m.toLowerCase()}`)}</option>
            ))}
          </select>
          <input
            type="number"
            min={0}
            value={discount}
            onChange={(e) => setDiscount(e.target.value)}
            placeholder={t('pos.discountFlat')}
            className="w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-900"
          />
          <div className="flex justify-between text-sm">
            <span>{t('pos.subtotal')}</span>
            <span>{formatLkr(cartSubtotal)}</span>
          </div>
          {discountAmount > 0 && (
            <div className="flex justify-between text-sm text-emerald-500">
              <span>{t('pos.discount')}</span>
              <span>−{formatLkr(discountAmount)}</span>
            </div>
          )}
          <div className="flex justify-between text-lg font-semibold text-glow-300">
            <span>{t('pos.total')}</span>
            <span>{formatLkr(cartTotal)}</span>
          </div>
          <button
            type="button"
            disabled={!cart.length || checkoutMut.isPending}
            onClick={() => checkoutMut.mutate()}
            className="w-full rounded-xl bg-glow-600 py-3 text-sm font-semibold text-white disabled:opacity-50"
          >
            {checkoutMut.isPending ? t('common.saving') : t('pos.completeSale')}
          </button>
          {lastSale && (
            <p className="text-center text-xs text-emerald-500">
              {t('pos.lastSale', { number: lastSale.saleNumber, amount: formatLkr(lastSale.totalAmount) })}
            </p>
          )}
        </div>

        {recentSales.length > 0 && (
          <div className="max-h-40 overflow-y-auto border-t border-zinc-200 p-4 dark:border-zinc-800">
            <p className="mb-2 text-xs font-medium text-zinc-500">{t('pos.recentSales')}</p>
            {recentSales.slice(0, 8).map((sale) => (
              <div key={sale._id} className="mb-2 flex items-center justify-between text-xs">
                <span className={sale.status === 'VOIDED' ? 'line-through text-zinc-500' : ''}>
                  {sale.saleNumber} · {formatLkr(sale.totalAmount)}
                </span>
                {sale.status === 'COMPLETED' && (
                  <button
                    type="button"
                    onClick={() => setVoidTarget(sale)}
                    className="text-red-400"
                  >
                    {t('pos.void')}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </aside>

      {staffPick && (
        <>
          <div className="fixed inset-0 z-40 bg-black/50" onClick={() => setStaffPick(null)} />
          <div className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-xl border border-zinc-200 bg-white p-5 dark:border-zinc-800 dark:bg-zinc-900">
            <h3 className="font-semibold">{staffPick.service.name}</h3>
            <p className="mt-1 text-sm text-zinc-500">{t('pos.pickStaff')}</p>
            <div className="mt-4 space-y-2">
              <button
                type="button"
                onClick={() => addServiceToCart(staffPick.service, null, 0, '')}
                className="w-full rounded-lg border border-zinc-300 py-2 text-sm dark:border-zinc-700"
              >
                {t('pos.noStaff')} · {formatLkr(staffPick.service.price)}
              </button>
              {staffPick.eligible.map((s) => {
                const extra = staffExtraForService(s, staffPick.service._id);
                const total = Number(staffPick.service.price) + extra;
                return (
                  <button
                    key={s._id}
                    type="button"
                    onClick={() =>
                      addServiceToCart(
                        staffPick.service,
                        s._id,
                        extra,
                        `${s.firstName} ${s.lastName}`.trim()
                      )
                    }
                    className="w-full rounded-lg border border-glow-600/30 bg-glow-600/10 py-2 text-sm"
                  >
                    {s.firstName} {s.lastName}
                    {extra > 0 ? ` (+${formatLkr(extra)})` : ''} · {formatLkr(total)}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}

      {voidTarget && (
        <>
          <div className="fixed inset-0 z-40 bg-black/50" onClick={() => setVoidTarget(null)} />
          <div className="fixed left-1/2 top-1/2 z-50 w-full max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-xl border border-zinc-200 bg-white p-5 dark:border-zinc-800 dark:bg-zinc-900">
            <h3 className="font-semibold">{t('pos.voidTitle')}</h3>
            <p className="mt-1 text-sm text-zinc-500">{voidTarget.saleNumber}</p>
            <textarea
              value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
              rows={3}
              className="mt-3 w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-950"
              placeholder={t('pos.voidReason')}
            />
            <div className="mt-4 flex gap-2">
              <button type="button" onClick={() => setVoidTarget(null)} className="flex-1 rounded-lg border py-2 text-sm">
                {t('common.cancel')}
              </button>
              <button
                type="button"
                disabled={!voidReason.trim() || voidMut.isPending}
                onClick={() => voidMut.mutate({ id: voidTarget._id, reason: voidReason })}
                className="flex-1 rounded-lg bg-red-600 py-2 text-sm text-white disabled:opacity-50"
              >
                {t('pos.confirmVoid')}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SalonPosPage;
